import { useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Link as DSLink,
  Icon,
  Item,
  Listbox,
  Span,
} from "@icg360/design-system";

import LiveChatButton from "components/live-chat-button";
import { AuthAppContext } from "components/root/auth-app-provider";

import styles from "./mobile-nav-menu.module.scss";

type MobileNavMenuProps = {
  onClose: () => void;
  onPolicySelector: () => void;
};

export const MobileNavMenu = ({
  onClose,
  onPolicySelector,
}: MobileNavMenuProps) => {
  const navigate = useNavigate();
  const { selectedPolicyId, userDetails } = useContext(AuthAppContext);
  const { pathname } = useLocation();

  const onSelectionChange = useCallback(
    (selection: Set<string>) => {
      onClose();
      const path = selection.keys().next().value;
      navigate(path);
    },
    [navigate, onClose]
  );

  return (
    <div className={styles.wrapper}>
      <div>
        <button
          className={styles.currentPolicyBtn}
          onClick={onPolicySelector}
          data-testid="mobile-policy-select-btn"
        >
          <Span bold>{userDetails?.propertyAddress?.street1}</Span>
          {userDetails?.propertyAddress?.street2 ? (
            <Span bold>{userDetails?.propertyAddress?.street2}</Span>
          ) : null}
          <Span>{`(Policy #${selectedPolicyId})`}</Span>
          <DSLink className={styles.switchLinkDS}>
            Switch to a different policy
          </DSLink>
        </button>
        <Listbox
          className={styles.navBoxDS}
          selectedKeys={new Set([pathname])}
          selectionMode="single"
          onSelectionChange={onSelectionChange}
          aria-label="main navigation"
        >
          <Item key="/my/overview" textValue="overview">
            <div className={styles.navItem}>
              <Icon name="Overview" />
              Overview
            </div>
          </Item>
          <Item key="/my/billing" textValue="billing">
            <div className={styles.navItem}>
              <Icon name="Money" />
              Billing
            </div>
          </Item>
          <Item key="/my/documents" textValue="documents">
            <div className={styles.navItem}>
              <Icon name="Notes" />
              Documents
            </div>
          </Item>
          <Item key="/my/claims" textValue="claims">
            <div className={styles.navItem}>
              <Icon name="Claims" />
              Claims
            </div>
          </Item>
          <Item key="/my/property" textValue="property">
            <div className={styles.navItem}>
              <Icon name="Home" />
              Property
            </div>
          </Item>
          <Item key="/my/support" textValue="support">
            <div className={styles.navItem}>
              <Icon name="Support" />
              Support
            </div>
          </Item>
        </Listbox>
      </div>
      <div className={styles.liveChatWrapper}>
        <LiveChatButton location="MobileNavMenu" size="default" fluid />
      </div>
    </div>
  );
};
